import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PelicanLogo } from '../components/PelicanLogo';
import { toast } from 'sonner';

export const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await register(email, password, name);
    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <PelicanLogo className="h-12 w-12 text-primary" strokeWidth={1.5} />
              <span className="text-3xl font-semibold tracking-tight">ArdNotes</span>
            </Link>
            <h2 className="mt-6 text-3xl font-semibold tracking-tight text-main">Create your account</h2>
            <p className="mt-2 text-sm text-muted">Join thousands of students sharing resources</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-testid="register-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>

            <p className="text-center text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary hover:text-primary-hover" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 relative items-center justify-center bg-gradient-to-br from-emerald-50 to-white dark:from-slate-900 dark:to-slate-950 border-l border-border">
        <div className="text-center p-12 max-w-md">
          <PelicanLogo className="h-32 w-32 text-primary mx-auto mb-8 opacity-80" strokeWidth={1.5} />
          <h3 className="text-3xl font-semibold tracking-tight mb-4">Join the ArdNotes community</h3>
          <p className="text-foreground/70 leading-relaxed">
            Share what you've learned, discover what others have shared, and turn revision into a collaborative effort.
          </p>
        </div>
      </div>
    </div>
  );
};